// Plano de trabalho do contrato (Operacional → Contratos → abrir contrato → Plano de trabalho).
// Doc: docs/plano-trabalho.md. O servidor é quem manda (handlers/planoTrabalho.js):
// a tela avisa antes, mas quem permite ou recusa é a resposta da API.
//
// O rascunho em edição mora no controlador (nível do contrato), não na aba:
// trocar de aba para conferir Atividades ou Drive não pode apagar o que foi digitado.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert, Button, Card, Col, Collapse, Divider, Empty, Flex, Input, Popconfirm,
  Progress, Row, Select, Space, Spin, Table, Tag, Tooltip, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined, DeleteOutlined, EditOutlined, LinkOutlined, PlusOutlined,
  ReloadOutlined, SaveOutlined, WarningOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { fmtDataBr } from '../../utils/dataFmt';
import { getToken } from './utils';
import type {
  EtapaPlanoTrabalho, ModoEtapaPlano, PlanoTrabalho, PlanoTrabalhoSnapshot,
} from './types';
import {
  ROTULOS_FONTE, ROTULO_PAPEL, ROTULO_STATUS_ATIVIDADE, divergenciasVinculo, ligarAtividadeExistente,
  mensagemErro, pendenciasEtapa, removerEtapa,
} from './planoTrabalhoRegras';

const ENDPOINT = `${API_URL}/comercial/propostas-geradas`;
const MAX_ETAPAS = 30;
type Papel = 'gestor' | 'coordenador';
const PAPEIS: Papel[] = ['gestor', 'coordenador'];

function corpo<T>(data: any): T {
  return data?.body ? JSON.parse(data.body) : data;
}

function erroCancelado() {
  const erro = new Error('Requisição anterior descartada') as Error & { cancelado: boolean };
  erro.cancelado = true;
  return erro;
}

export interface EdicaoPlano {
  planoId: string;
  revisao: number;
  objetivo: string;
  etapas: EtapaPlanoTrabalho[];
}

export interface PlanoTrabalhoController {
  snapshot: PlanoTrabalhoSnapshot | null;
  loading: boolean;
  negado: boolean;
  implantando: boolean;
  erro: string | null;
  edicao: EdicaoPlano | null;
  setEdicao: (e: EdicaoPlano | null) => void;
  executar: (action: string, payload?: Record<string, unknown>) => Promise<PlanoTrabalhoSnapshot>;
  recarregar: () => Promise<void>;
}

export function usePlanoTrabalho(contratoId: string, habilitado: boolean): PlanoTrabalhoController {
  const [snapshot, setSnapshot] = useState<PlanoTrabalhoSnapshot | null>(null);
  const [loading, setLoading] = useState(habilitado);
  const [negado, setNegado] = useState(false);
  const [implantando, setImplantando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [edicao, setEdicao] = useState<EdicaoPlano | null>(null);
  const sequencia = useRef(0);

  const executar = useCallback(async (action: string, payload: Record<string, unknown> = {}) => {
    const requisicao = ++sequencia.current;
    let resposta;
    try {
      const token = await getToken();
      resposta = await axios.post(ENDPOINT,
        { action, contrato_id: contratoId, ...payload },
        { headers: { Authorization: token } });
    } catch (e: any) {
      if (requisicao !== sequencia.current) throw erroCancelado();
      if (e.response?.status === 403 || (action === 'op_plano_obter' && e.response?.status === 404)) {
        setSnapshot(null);
        setNegado(true);
      }
      throw e;
    }
    if (requisicao !== sequencia.current) throw erroCancelado();
    const proximo = corpo<PlanoTrabalhoSnapshot>(resposta.data);
    setSnapshot(proximo);
    setErro(null);
    setNegado(false);
    setImplantando(false);
    return proximo;
  }, [contratoId]);

  const recarregar = useCallback(async () => {
    if (!habilitado) return;
    setLoading(true);
    setSnapshot(null);
    let cancelado = false;
    try {
      await executar('op_plano_obter');
    } catch (e: any) {
      if (e.cancelado) { cancelado = true; return; }
      const status = e.response?.status;
      setNegado(status === 403 || status === 404);
      setImplantando(status === 503);
      if (status !== 403 && status !== 404 && status !== 503) setErro(mensagemErro(e));
    } finally { if (!cancelado) setLoading(false); }
  }, [executar, habilitado]);

  useEffect(() => {
    setSnapshot(null);
    setNegado(false);
    setImplantando(false);
    setErro(null);
    setEdicao(null);
    void recarregar();
    return () => { sequencia.current += 1; };
  }, [recarregar]);

  return { snapshot, loading, negado, implantando, erro, edicao, setEdicao, executar, recarregar };
}

const STATUS_PLANO = {
  rascunho: { texto: 'Em preparação', cor: 'gold' },
  aprovado: { texto: 'Aprovado', cor: 'green' },
  substituido: { texto: 'Versão anterior', cor: 'default' },
} as const;

const MODOS: Array<{ value: ModoEtapaPlano; label: string }> = [
  { value: 'humana', label: 'Trabalho da equipe' },
  { value: 'assistida', label: 'Com apoio de IA' },
  { value: 'ferramenta_pendente', label: 'Ferramenta a preparar' },
];
const rotuloModo = (m: ModoEtapaPlano) => MODOS.find(x => x.value === m)?.label || m;

function fmtInstante(iso: string) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function novoId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function novaEtapa(): EtapaPlanoTrabalho {
  return {
    id: novoId(), titulo: '', descricao: '', modo: 'humana', responsavel_id: null, data_inicio: null,
    data_fim: null, entradas: '', resultado_esperado: '', criterio_aceite: '', dependencias: [], atividade_id: null,
  };
}

const pendentesTexto = (plano: PlanoTrabalho) => PAPEIS
  .filter(p => !plano.aprovacoes.some(a => a.papel === p))
  .map(p => ROTULO_PAPEL[p].toLowerCase());

// ── Cartão resumido na Visão geral ────────────────────────────────────────
export function ResumoPlanoTrabalho({ controller, abrirPlano }: {
  controller: PlanoTrabalhoController;
  abrirPlano: () => void;
}) {
  const { snapshot, loading, negado, implantando, erro } = controller;
  if (negado || (!loading && snapshot && !snapshot.permissoes.ver)) return null;
  if (!loading && !snapshot && !implantando && !erro) return null;

  const plano = snapshot?.plano || snapshot?.plano_vigente;
  const vigente = snapshot?.plano_vigente;
  const concluidas = vigente?.etapas.filter(e =>
    snapshot?.atividades.find(a => a.id === e.atividade_id)?.status === 'concluida').length || 0;
  const total = vigente?.etapas.length || 0;
  const status = plano ? STATUS_PLANO[plano.status] : null;

  let linha = 'Nenhum plano ainda. O plano-base traz cinco etapas para ajustar.';
  if (loading) linha = 'Carregando…';
  else if (implantando) linha = 'Funcionalidade ainda em implantação.';
  else if (erro) linha = 'Não foi possível consultar o plano agora.';
  else if (plano?.status === 'rascunho') {
    const faltam = pendentesTexto(plano);
    linha = `${plano.etapas.length} etapa${plano.etapas.length === 1 ? '' : 's'} · aguardando aprovação de ${faltam.join(' e ')}`;
  } else if (plano) linha = `${total} etapa${total === 1 ? '' : 's'} · aprovado por gestor e coordenador`;

  return (
    <Card size="small" style={{ marginBottom: 12, borderColor: '#C9D2FF' }}>
      <Flex gap={12} align="center" justify="space-between" wrap="wrap">
        <div style={{ minWidth: 220, flex: 1 }}>
          <Space wrap>
            <Typography.Text strong style={{ color: '#020D7F' }}>Plano de trabalho</Typography.Text>
            {status && <Tag color={status.cor}>{status.texto}</Tag>}
            {plano && <Typography.Text type="secondary">versão {plano.versao}</Typography.Text>}
          </Space>
          <div><Typography.Text type="secondary">{linha}</Typography.Text></div>
          {vigente && total > 0 && (
            <Progress percent={Math.round((concluidas / total) * 100)} size="small"
              format={() => `${concluidas}/${total} atividades concluídas`} />
          )}
        </div>
        <Button type="primary" ghost onClick={abrirPlano} disabled={loading || implantando}>
          {plano ? 'Abrir plano' : 'Ver plano de trabalho'}
        </Button>
      </Flex>
    </Card>
  );
}

// ── Aba completa ──────────────────────────────────────────────────────────
export function PlanoTrabalhoView({ controller, navegar, mostrarDadosCampo = false, onAtividadesAlteradas }: {
  controller: PlanoTrabalhoController;
  navegar: (aba: 'atividades' | 'dados_campo' | 'drive' | 'entregaveis') => void;
  mostrarDadosCampo?: boolean;
  /** Chamado quando a aprovação cria/vincula atividades (atualiza a aba Atividades). */
  onAtividadesAlteradas?: () => void;
}) {
  const { snapshot, loading, negado, implantando, erro, edicao, setEdicao, executar, recarregar } = controller;
  const [salvando, setSalvando] = useState(false);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [conflito, setConflito] = useState(false);
  const [avisosVinculo, setAvisosVinculo] = useState<Record<string, string[]>>({});
  const plano = snapshot?.plano || null;

  const base = useMemo(() => (plano ? {
    objetivo: plano.objetivo || '',
    etapas: (plano.etapas || []).map(e => ({ ...e, dependencias: [...(e.dependencias || [])] })),
  } : null), [plano]);
  const emEdicao = edicao && plano && edicao.planoId === plano.id && edicao.revisao === plano.revisao ? edicao : null;
  const objetivo = emEdicao ? emEdicao.objetivo : base?.objetivo ?? '';
  const etapas = emEdicao ? emEdicao.etapas : base?.etapas ?? [];
  const temAlteracoes = !!emEdicao && JSON.stringify({ objetivo, etapas }) !== JSON.stringify(base);

  useEffect(() => { setConflito(false); setErroAcao(null); setAvisosVinculo({}); }, [plano?.id, plano?.revisao]);

  const usuarios = snapshot?.usuarios || [];
  const atividades = snapshot?.atividades || [];
  const editavel = !!(plano?.status === 'rascunho' && snapshot?.permissoes.editar);
  const papeisFeitos = new Set(plano?.aprovacoes.map(a => a.papel) || []);
  const papeisDisponiveis = (snapshot?.permissoes.aprovar ? snapshot.papeis_aprovacao : []).filter(p => !papeisFeitos.has(p));
  const pendencias = etapas.map(e => ({
    etapa: e,
    faltas: pendenciasEtapa(e, usuarios),
    divergencias: divergenciasVinculo(e, atividades.find(a => a.id === e.atividade_id), usuarios, etapas),
  }));
  const etapasComProblema = pendencias.filter(p => p.faltas.length || p.divergencias.length);
  const aprovacaoBloqueada = !objetivo.trim() || etapas.length === 0 || etapasComProblema.length > 0;
  const novasNaAprovacao = etapas.filter(e => !e.atividade_id).length;

  const opcoesDependencia = useMemo(() => etapas.map((e, i) => ({
    value: e.id, label: `${i + 1}. ${e.titulo || 'Etapa sem título'}`,
  })), [etapas]);
  const numeroDaEtapa = (id: string) => etapas.findIndex(e => e.id === id) + 1;

  function alterar(mudanca: { objetivo?: string; etapas?: EtapaPlanoTrabalho[] }) {
    if (!plano) return;
    setEdicao({
      planoId: plano.id, revisao: plano.revisao,
      objetivo: mudanca.objetivo ?? objetivo, etapas: mudanca.etapas ?? etapas,
    });
  }
  const alterarEtapa = (id: string, campos: Partial<EtapaPlanoTrabalho>) =>
    alterar({ etapas: etapas.map(x => (x.id === id ? { ...x, ...campos } : x)) });

  function escolherAtividade(etapa: EtapaPlanoTrabalho, atividadeId: string | undefined) {
    if (!atividadeId) {
      alterarEtapa(etapa.id, { atividade_id: null });
      setAvisosVinculo(a => ({ ...a, [etapa.id]: [] }));
      return;
    }
    const atividade = atividades.find(a => a.id === atividadeId);
    if (!atividade) return;
    const r = ligarAtividadeExistente(etapa, atividade, usuarios, etapas);
    alterar({ etapas: etapas.map(x => (x.id === etapa.id ? r.etapa : x)) });
    setAvisosVinculo(a => ({ ...a, [etapa.id]: r.avisos }));
  }

  async function acao(action: string, payload: Record<string, unknown> = {}) {
    setSalvando(true);
    setErroAcao(null);
    try {
      const proximo = await executar(action, payload);
      setConflito(false);
      return proximo;
    } catch (e: any) {
      if (e.cancelado) throw e;
      const status = e.response?.status;
      if (status === 409) setConflito(true);
      setErroAcao(status === 503 ? 'Funcionalidade ainda em implantação. Tente novamente mais tarde.' : mensagemErro(e));
      throw e;
    } finally { setSalvando(false); }
  }

  async function preparar() {
    try { await acao('op_plano_preparar'); } catch { /* mensagem já na tela */ }
  }
  async function salvar() {
    if (!plano) return;
    if (!objetivo.trim()) { setErroAcao('Informe o objetivo do plano.'); return; }
    if (etapas.some(e => !e.titulo.trim())) { setErroAcao('Dê um título a todas as etapas antes de salvar.'); return; }
    try {
      await acao('op_plano_salvar', { plano_id: plano.id, revisao: plano.revisao, objetivo: objetivo.trim(), etapas });
      setEdicao(null);
    } catch { /* rascunho continua na tela */ }
  }
  async function aprovar(papel: Papel) {
    if (!plano) return;
    if (temAlteracoes) { setErroAcao('Salve as alterações do rascunho antes de aprovar.'); return; }
    try {
      const proximo = await acao('op_plano_aprovar', { plano_id: plano.id, revisao: plano.revisao, papel });
      setEdicao(null);
      if (proximo.plano?.status === 'aprovado') onAtividadesAlteradas?.();
    } catch { /* mensagem já na tela */ }
  }
  async function novaVersao() {
    if (!plano) return;
    try { await acao('op_plano_revisar', { plano_id: plano.id, revisao: plano.revisao }); } catch { /* idem */ }
  }
  async function trocarVersao(planoId: string) {
    if (planoId === plano?.id) return;
    if (temAlteracoes && !window.confirm('Trocar de versão e descartar as alterações que ainda não foram salvas?')) return;
    setEdicao(null);
    try { await acao('op_plano_obter', { plano_id: planoId }); } catch { /* idem */ }
  }

  if (loading) return <Flex justify="center" style={{ padding: 48 }}><Spin description="Carregando plano…" /></Flex>;
  if (negado || (snapshot && !snapshot.permissoes.ver)) {
    return <Empty description="Plano de trabalho indisponível para você neste contrato. Quem libera é a Administração → Permissões, e é preciso fazer parte da equipe do contrato." />;
  }
  if (implantando) {
    return <Alert type="info" showIcon title="Funcionalidade ainda em implantação"
      description="O plano ficará disponível aqui quando a publicação do servidor terminar." />;
  }
  if (erro && !snapshot) {
    return <Alert type="error" showIcon title="Não foi possível carregar o plano" description={erro}
      action={<Button onClick={() => void recarregar()}>Tentar de novo</Button>} />;
  }
  if (!snapshot) return null;

  if (!plano) {
    return (
      <Card>
        <Empty description="Este contrato ainda não tem plano de trabalho." image={Empty.PRESENTED_IMAGE_SIMPLE}>
          {snapshot.permissoes.editar
            ? <Button type="primary" onClick={() => void preparar()} loading={salvando}>Criar plano-base</Button>
            : <Typography.Text type="secondary">Quem tem permissão de preparar planos pode criar o plano-base.</Typography.Text>}
        </Empty>
        {erroAcao && <Alert style={{ marginTop: 12 }} type="error" showIcon title={erroAcao} />}
        <Alert style={{ marginTop: 16 }} type="info" showIcon
          title="O plano-base traz cinco etapas para ajustar e guarda uma cópia do briefing como referência. Nada é criado em Atividades até o gestor e o coordenador aprovarem." />
      </Card>
    );
  }

  const status = STATUS_PLANO[plano.status];
  const vendoVersaoAntiga = plano.status === 'substituido' || (plano.status === 'aprovado' && !!snapshot.historico?.some(h => h.status === 'rascunho'));
  const versaoAtual = snapshot.historico?.find(h => h.status === 'rascunho') || snapshot.historico?.find(h => h.status === 'aprovado');

  const colunasLeitura: ColumnsType<EtapaPlanoTrabalho> = [
    { title: '#', key: 'n', width: 44, render: (_, __, i) => i + 1 },
    {
      title: 'Etapa', key: 'etapa', width: '24%',
      render: (_, e) => (
        <div>
          <Typography.Text strong>{e.titulo}</Typography.Text>
          <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>{rotuloModo(e.modo)}</Typography.Text></div>
          {e.descricao && <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }} ellipsis={{ rows: 2, expandable: true, symbol: 'mais' }}>{e.descricao}</Typography.Paragraph>}
        </div>
      ),
    },
    {
      title: 'Responsável', key: 'resp', width: '14%',
      render: (_, e) => {
        const u = usuarios.find(x => x.id === e.responsavel_id);
        return u ? (u.nome || u.email) : <Typography.Text type="warning">Fora da equipe atual</Typography.Text>;
      },
    },
    {
      title: 'Prazo', key: 'prazo', width: 120,
      render: (_, e) => <span>{e.data_inicio ? `${fmtDataBr(e.data_inicio)} a ` : 'até '}{fmtDataBr(e.data_fim)}</span>,
    },
    {
      title: 'Depende de', key: 'deps', width: 90,
      render: (_, e) => (e.dependencias.length ? e.dependencias.map(numeroDaEtapa).filter(n => n > 0).join(', ') : '—'),
    },
    {
      title: 'Resultado e como conferir', key: 'resultado',
      render: (_, e) => (
        <div style={{ fontSize: 12 }}>
          <div><Typography.Text strong style={{ fontSize: 12 }}>Resultado: </Typography.Text>{e.resultado_esperado || '—'}</div>
          <div><Typography.Text strong style={{ fontSize: 12 }}>Aceite: </Typography.Text>{e.criterio_aceite || '—'}</div>
          {e.entradas && <div><Typography.Text strong style={{ fontSize: 12 }}>Entradas: </Typography.Text>{e.entradas}</div>}
        </div>
      ),
    },
    {
      title: 'Atividade', key: 'atividade', width: '18%',
      render: (_, e) => {
        const at = atividades.find(a => a.id === e.atividade_id);
        if (!e.atividade_id) {
          return <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {plano.status === 'rascunho' ? 'Será criada na aprovação' : '—'}
          </Typography.Text>;
        }
        if (!at) return <Typography.Text type="warning" style={{ fontSize: 12 }}>Atividade excluída</Typography.Text>;
        return (
          <div>
            <Typography.Text style={{ fontSize: 12 }}>{at.titulo}</Typography.Text>
            <div>
              <Tag color={at.status === 'concluida' ? 'green' : at.status === 'em_andamento' ? 'blue' : 'default'}>
                {ROTULO_STATUS_ATIVIDADE[at.status] || at.status}
              </Tag>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>{at.progresso_pct || 0}%</Typography.Text>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <Space orientation="vertical" size={12} style={{ width: '100%' }}>
      {conflito && (
        <Alert type="warning" showIcon title="A ação não foi concluída"
          description="O que você digitou continua nesta tela. Leia a orientação em vermelho; recarregue só se precisar ver o que está gravado agora (recarregar descarta o que não foi salvo)."
          action={<Button icon={<ReloadOutlined />} onClick={() => { setConflito(false); setEdicao(null); void recarregar(); }}>Recarregar</Button>} />
      )}
      {erroAcao && <Alert closable={{ onClose: () => setErroAcao(null) }} type="error" showIcon title={erroAcao} />}
      {vendoVersaoAntiga && versaoAtual && versaoAtual.id !== plano.id && (
        <Alert type="info" showIcon title={`Você está vendo a versão ${plano.versao}.`}
          action={<Button size="small" onClick={() => void trocarVersao(versaoAtual.id)}>Ir para a versão {versaoAtual.versao}</Button>} />
      )}
      {plano.status === 'rascunho' && snapshot.plano_vigente && (
        <Alert type="info" showIcon title={`A versão ${snapshot.plano_vigente.versao} continua valendo`}
          description="Este rascunho só passa a valer depois das aprovações do gestor e do coordenador. As atividades já ligadas continuam como estão." />
      )}

      <Card size="small">
        <Flex justify="space-between" gap={12} wrap="wrap" align="center">
          <Space wrap>
            <Typography.Title level={4} style={{ margin: 0 }}>Plano de trabalho</Typography.Title>
            <Tag color={status.cor}>{status.texto}</Tag>
            <Typography.Text type="secondary">versão {plano.versao}</Typography.Text>
            {temAlteracoes && <Tag color="orange">alterações não salvas</Tag>}
          </Space>
          <Space wrap>
            {(snapshot.historico?.length || 0) > 1 && (
              <Select aria-label="Versão do plano" value={plano.id} disabled={salvando} style={{ minWidth: 200 }}
                onChange={v => void trocarVersao(v)}
                options={(snapshot.historico || []).map(item => ({
                  value: item.id,
                  label: `Versão ${item.versao} · ${STATUS_PLANO[item.status].texto}`,
                }))} />
            )}
            {editavel && (plano.aprovacoes.length > 0 ? (
              <Popconfirm title="Salvar e recomeçar as aprovações?"
                description="Qualquer alteração no rascunho apaga as aprovações já dadas; gestor e coordenador aprovam de novo."
                okText="Salvar" cancelText="Voltar" onConfirm={() => void salvar()}>
                <Button type="primary" icon={<SaveOutlined />} loading={salvando} disabled={!temAlteracoes}>Salvar rascunho</Button>
              </Popconfirm>
            ) : (
              <Button type="primary" icon={<SaveOutlined />} loading={salvando} disabled={!temAlteracoes}
                onClick={() => void salvar()}>Salvar rascunho</Button>
            ))}
            {plano.status === 'aprovado' && snapshot.permissoes.editar && !snapshot.historico?.some(h => h.status === 'rascunho') && (
              <Popconfirm title="Criar uma nova versão do plano?"
                description="A versão aprovada continua valendo até a nova ser aprovada por gestor e coordenador."
                okText="Criar" cancelText="Voltar" onConfirm={() => void novaVersao()}>
                <Button icon={<EditOutlined />} loading={salvando}>Criar nova versão</Button>
              </Popconfirm>
            )}
            <Tooltip title="Buscar de novo o que está gravado">
              <Button icon={<ReloadOutlined />} disabled={salvando} aria-label="Recarregar plano"
                onClick={() => {
                  if (temAlteracoes && !window.confirm('Recarregar descarta as alterações que ainda não foram salvas. Continuar?')) return;
                  setEdicao(null);
                  void recarregar();
                }} />
            </Tooltip>
          </Space>
        </Flex>
        <Divider style={{ margin: '12px 0' }} />
        <Row gutter={[16, 12]}>
          <Col xs={24} lg={14}>
            <Typography.Text strong>Objetivo</Typography.Text>
            {editavel ? (
              <Input.TextArea value={objetivo} onChange={e => alterar({ objetivo: e.target.value })}
                autoSize={{ minRows: 2, maxRows: 6 }} maxLength={6000} placeholder="O que este plano precisa entregar?" />
            ) : <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{objetivo}</Typography.Paragraph>}
          </Col>
          <Col xs={24} lg={10}>
            <Typography.Text strong>Aprovações</Typography.Text>
            <div style={{ marginTop: 4 }}>
              {PAPEIS.map(papel => {
                const feita = plano.aprovacoes.find(a => a.papel === papel);
                const ocupante = snapshot.aprovadores?.[papel];
                return (
                  <div key={papel} style={{ marginBottom: 4 }}>
                    {feita ? (
                      <Tag icon={<CheckCircleOutlined />} color="green">
                        {ROTULO_PAPEL[papel]}: {feita.ator_nome || feita.ator_email} · {fmtInstante(feita.aprovado_em)}
                      </Tag>
                    ) : (
                      <Tag>{ROTULO_PAPEL[papel]}: pendente{ocupante ? ` (${ocupante})` : ' — sem pessoa definida no contrato'}</Tag>
                    )}
                  </div>
                );
              })}
              {plano.status === 'aprovado' && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Aprovar organizou as atividades. Não iniciou o campo, não entregou produto e não gerou medição nem faturamento.
                </Typography.Text>
              )}
            </div>
          </Col>
        </Row>
      </Card>

      <Collapse size="small" items={[{
        key: 'fonte', label: 'Base do briefing usada neste plano (referência)',
        children: (
          <Row gutter={[16, 12]}>
            {Object.keys(ROTULOS_FONTE).map(chave => (
              <Col xs={24} md={12} xl={8} key={chave}>
                <Typography.Text strong>{ROTULOS_FONTE[chave]}</Typography.Text>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  <Typography.Text type="secondary">{(plano.fonte as any)?.[chave] || 'Não informado no briefing'}</Typography.Text>
                </div>
              </Col>
            ))}
          </Row>
        ),
      }]} />

      <Flex justify="space-between" align="center" gap={8} wrap="wrap">
        <Space wrap>
          <Typography.Title level={5} style={{ margin: 0 }}>Etapas ({etapas.length}/{MAX_ETAPAS})</Typography.Title>
          {plano.status === 'rascunho' && (etapasComProblema.length
            ? <Tag icon={<WarningOutlined />} color="orange">{etapasComProblema.length} etapa{etapasComProblema.length === 1 ? '' : 's'} com dados faltando</Tag>
            : <Tag color="green">todas as etapas completas</Tag>)}
        </Space>
        {editavel && (
          <Button icon={<PlusOutlined />} disabled={etapas.length >= MAX_ETAPAS}
            onClick={() => alterar({ etapas: [...etapas, novaEtapa()] })}>Adicionar etapa</Button>
        )}
      </Flex>

      {!editavel && (
        <Table<EtapaPlanoTrabalho> size="small" rowKey="id" columns={colunasLeitura} dataSource={etapas}
          pagination={false} scroll={{ x: 900 }} locale={{ emptyText: 'Nenhuma etapa.' }} />
      )}

      {editavel && (etapas.length === 0 ? <Empty description="Nenhuma etapa. Use “Adicionar etapa”." /> : etapas.map((etapa, indice) => {
        const { faltas, divergencias } = pendencias[indice];
        const avisos = avisosVinculo[etapa.id] || [];
        return (
          <Card key={etapa.id} size="small"
            title={<Space wrap>
              <span>{indice + 1}. {etapa.titulo || 'Etapa sem título'}</span>
              {faltas.length > 0 && <Tag color="orange">falta: {faltas.join(', ')}</Tag>}
            </Space>}
            extra={
              <Popconfirm title="Remover esta etapa?"
                description={etapa.atividade_id ? 'A atividade já cadastrada continua existindo; só deixa de estar no plano.' : undefined}
                okText="Remover" cancelText="Voltar"
                onConfirm={() => alterar({ etapas: removerEtapa(etapas, etapa.id) })}>
                <Button type="text" danger icon={<DeleteOutlined />} aria-label="Remover etapa" />
              </Popconfirm>
            }>
            <Row gutter={[12, 10]}>
              <Col xs={24} md={12} xl={8}>
                <Typography.Text strong>Título</Typography.Text>
                <Input value={etapa.titulo} maxLength={250} placeholder="Ex.: Batimetria da baía"
                  onChange={e => alterarEtapa(etapa.id, { titulo: e.target.value })} />
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Typography.Text strong>Responsável</Typography.Text>
                <Select style={{ width: '100%' }} value={etapa.responsavel_id} allowClear showSearch
                  optionFilterProp="label" placeholder="Pessoa da equipe do contrato"
                  status={etapa.responsavel_id && !usuarios.some(u => u.id === etapa.responsavel_id) ? 'warning' : undefined}
                  options={usuarios.map(u => ({ value: u.id, label: u.nome && u.nome !== u.email ? `${u.nome} · ${u.email}` : u.email }))}
                  notFoundContent="Ninguém com esse nome na equipe ativa do contrato"
                  onChange={v => alterarEtapa(etapa.id, { responsavel_id: v || null })} />
              </Col>
              <Col xs={24} md={8} xl={4}>
                <Typography.Text strong>Como será feita</Typography.Text>
                <Select style={{ width: '100%' }} value={etapa.modo} options={MODOS}
                  onChange={v => alterarEtapa(etapa.id, { modo: v })} />
              </Col>
              <Col xs={12} md={8} xl={3}>
                <Typography.Text strong>Início</Typography.Text>
                <Input type="date" value={etapa.data_inicio || ''}
                  onChange={e => alterarEtapa(etapa.id, { data_inicio: e.target.value || null })} />
              </Col>
              <Col xs={12} md={8} xl={3}>
                <Typography.Text strong>Fim</Typography.Text>
                <Input type="date" value={etapa.data_fim || ''} status={!etapa.data_fim ? 'warning' : undefined}
                  onChange={e => alterarEtapa(etapa.id, { data_fim: e.target.value || null })} />
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Typography.Text strong>Descrição</Typography.Text>
                <Input.TextArea value={etapa.descricao} autoSize={{ minRows: 2, maxRows: 5 }} maxLength={4000}
                  onChange={e => alterarEtapa(etapa.id, { descricao: e.target.value })} />
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Typography.Text strong>Entradas necessárias</Typography.Text>
                <Input.TextArea value={etapa.entradas} autoSize={{ minRows: 2, maxRows: 5 }} maxLength={3000}
                  placeholder="Dados, arquivos ou equipamentos" onChange={e => alterarEtapa(etapa.id, { entradas: e.target.value })} />
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Typography.Text strong>Resultado esperado</Typography.Text>
                <Input.TextArea value={etapa.resultado_esperado} autoSize={{ minRows: 2, maxRows: 5 }} maxLength={3000}
                  onChange={e => alterarEtapa(etapa.id, { resultado_esperado: e.target.value })} />
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Typography.Text strong>Critério de aceite</Typography.Text>
                <Input.TextArea value={etapa.criterio_aceite} autoSize={{ minRows: 2, maxRows: 5 }} maxLength={3000}
                  placeholder="Como a equipe confere que ficou pronto" onChange={e => alterarEtapa(etapa.id, { criterio_aceite: e.target.value })} />
              </Col>
              <Col xs={24} md={12}>
                <Typography.Text strong>Depende de</Typography.Text>
                <Select mode="multiple" style={{ width: '100%' }} value={etapa.dependencias}
                  options={opcoesDependencia.filter(o => o.value !== etapa.id)} placeholder="Etapas que precisam terminar antes"
                  onChange={v => alterarEtapa(etapa.id, { dependencias: v })} />
              </Col>
              <Col xs={24} md={12}>
                <Typography.Text strong>Atividade já cadastrada (opcional)</Typography.Text>
                <Select style={{ width: '100%' }} value={etapa.atividade_id} allowClear showSearch optionFilterProp="label"
                  placeholder="Deixe em branco para criar uma nova na aprovação"
                  options={atividades.map(a => ({
                    value: a.id,
                    label: `${a.titulo} · ${ROTULO_STATUS_ATIVIDADE[a.status] || a.status}`,
                    disabled: etapas.some(x => x.id !== etapa.id && x.atividade_id === a.id),
                  }))}
                  notFoundContent="Nenhuma atividade cadastrada neste contrato"
                  onChange={v => escolherAtividade(etapa, v)} />
              </Col>
            </Row>
            {etapa.atividade_id && (divergencias.length > 0 || avisos.length > 0) && (
              <Alert style={{ marginTop: 10 }} type="warning" showIcon
                title={divergencias.length
                  ? `Diferente da atividade cadastrada: ${divergencias.join(', ')}. O plano não altera a atividade; alinhe os dois antes de aprovar.`
                  : 'Dados copiados da atividade cadastrada.'}
                description={avisos.length ? `Atenção: ${avisos.join('; ')}.` : undefined} />
            )}
            {etapa.atividade_id && divergencias.length === 0 && avisos.length === 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                <LinkOutlined /> Ligada a uma atividade já cadastrada: datas, responsável e dependências conferem.
              </Typography.Text>
            )}
          </Card>
        );
      }))}

      {plano.status === 'rascunho' && (
        <Card size="small" style={{ borderColor: '#B7E3C9' }}>
          <Flex justify="space-between" align="center" gap={12} wrap="wrap">
            <div style={{ flex: 1, minWidth: 260 }}>
              <Typography.Text strong>Aprovação do plano</Typography.Text>
              <div>
                <Typography.Text type="secondary">
                  Com as duas aprovações, {novasNaAprovacao} atividade{novasNaAprovacao === 1 ? ' é criada' : 's são criadas'}
                  {etapas.length - novasNaAprovacao > 0 ? ` e ${etapas.length - novasNaAprovacao} já cadastrada${etapas.length - novasNaAprovacao === 1 ? ' é ligada' : 's são ligadas'}` : ''} em Atividades.
                  Não inicia o campo, não mexe em entregas e não gera medição nem faturamento.
                </Typography.Text>
              </div>
              <div style={{ marginTop: 6 }}>
                {temAlteracoes && <Alert type="warning" showIcon title="Salve o rascunho antes de aprovar." />}
                {!temAlteracoes && aprovacaoBloqueada && (
                  <Alert type="warning" showIcon title="Ainda não dá para aprovar"
                    description={!objetivo.trim() ? 'Informe o objetivo do plano.'
                      : etapasComProblema.map(p => {
                        const i = etapas.indexOf(p.etapa) + 1;
                        const itens = [...p.faltas, ...p.divergencias.map(d => `${d} diferente da atividade cadastrada`)];
                        return `Etapa ${i} (${p.etapa.titulo || 'sem título'}): ${itens.join(', ')}`;
                      }).join(' · ')} />
                )}
                {!temAlteracoes && !aprovacaoBloqueada && papeisDisponiveis.length === 0 && (
                  <Alert type="info" showIcon
                    title={pendentesTexto(plano).length
                      ? `Pronto. Falta a aprovação de ${pendentesTexto(plano).join(' e ')}, que precisa entrar com a própria conta.`
                      : 'Pronto para aprovar.'}
                    description={!snapshot.aprovadores?.gestor || !snapshot.aprovadores?.coordenador
                      ? 'Defina gestor e coordenador em “Editar dados” do contrato.' : undefined} />
                )}
              </div>
            </div>
            <Space wrap>
              {papeisDisponiveis.map(papel => {
                const ultima = PAPEIS.every(p => p === papel || papeisFeitos.has(p));
                return (
                  <Popconfirm key={papel} title={`Aprovar como ${ROTULO_PAPEL[papel].toLowerCase()}?`}
                    description={ultima
                      ? `Esta é a segunda aprovação: as atividades serão criadas/ligadas agora.`
                      : `Depois desta, falta a aprovação de ${pendentesTexto(plano).filter(p => p !== papel).join(' e ')}.`}
                    okText="Aprovar" cancelText="Voltar" onConfirm={() => void aprovar(papel)}
                    disabled={aprovacaoBloqueada || temAlteracoes}>
                    <Button type="primary" icon={<CheckCircleOutlined />} loading={salvando} disabled={aprovacaoBloqueada || temAlteracoes}>
                      Aprovar como {ROTULO_PAPEL[papel].toLowerCase()}
                    </Button>
                  </Popconfirm>
                );
              })}
            </Space>
          </Flex>
        </Card>
      )}

      <Card size="small">
        <Space wrap>
          <Typography.Text type="secondary">Abrir no contrato:</Typography.Text>
          <Button size="small" icon={<LinkOutlined />} onClick={() => navegar('atividades')}>Atividades</Button>
          {mostrarDadosCampo && <Button size="small" icon={<LinkOutlined />} onClick={() => navegar('dados_campo')}>Dados de campo</Button>}
          <Button size="small" icon={<LinkOutlined />} onClick={() => navegar('drive')}>Drive</Button>
          <Button size="small" icon={<LinkOutlined />} onClick={() => navegar('entregaveis')}>Entregáveis</Button>
        </Space>
      </Card>
    </Space>
  );
}
